import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CellRenderer, CellRendererParams } from 'app/shared/components/smart-grid/core/interfaces/cell-renderer.interface';
import { UnitMeasureDisplayComponent } from 'app/shared/components/unit-measure-display/unit-measure-display.component';

/**
 * Quantity Before Cell Renderer Component
 *
 * Muestra la cantidad "Antes" usando UnitMeasureDisplayComponent.
 * Solo lectura, sin ícono de cambio.
 *
 * Uso en ColDef:
 * ```typescript
 * {
 *   field: "soldQuantityBefore",
 *   headerName: "Cantidad Antes",
 *   cellRenderer: QuantityBeforeUnitCellRendererComponent,
 *   cellRendererParams: {
 *     soldBeforeField: 'soldQuantityBefore',
 *     baseBeforeField: 'baseQuantityBefore',
 *     parallelBeforeField: 'pararellQuantityBefore',
 *     soldUnitBeforeField: 'soldUnitOfMeasureBefore',
 *     baseUnitBeforeField: 'baseUnitOfMeasureBefore',
 *     parallelUnitBeforeField: 'pararellUnitOfMeasureBefore',
 *     getUMPActive: () => this.UMPActive,
 *   },
 * }
 * ```
 */
@Component({
  selector: 'app-quantity-before-unit-cell-renderer',
  standalone: true,
  imports: [CommonModule, UnitMeasureDisplayComponent],
  template: `
    <app-unit-measure-display
      [usedUnit]="{
        quantity: beforeUsedQuantity,
        unitId: beforeUsedUnit?._id,
        unitName: beforeUsedUnit?.name,
        unitAbbreviation: beforeUsedUnit?.code
      }"
      [baseUnit]="{
        quantity: beforeBaseQuantity,
        unitId: beforeBaseUnit?._id,
        unitName: beforeBaseUnit?.name,
        unitAbbreviation: beforeBaseUnit?.code
      }"
      [parallelUnit]="{
        quantity: beforeParallelQuantity,
        unitId: beforeParallelUnit?._id,
        unitName: beforeParallelUnit?.name,
        unitAbbreviation: beforeParallelUnit?.code
      }"
      [material]="{
        _id: material?._id,
        code: material?.code,
        UMPActive: material?.UMPActive ?? false
      }"
      [companyUMPActive]="isUMPActive"
    ></app-unit-measure-display>
  `,
  styles: [`
    :host {
      display: flex;
      align-items: center;
    }
  `]
})
export class QuantityBeforeUnitCellRendererComponent<T> implements CellRenderer<T, number> {
  /**
   * Parameters from the grid containing the cell value and row data
   */
  @Input() params!: CellRendererParams<T, number>;

  // ============================================
  // BEFORE (Antes) - Cantidades
  // ============================================

  get beforeUsedQuantity(): number {
    const field = this.params?.colDef?.cellRendererParams?.soldBeforeField as string;
    return this.params?.data?.[field] ?? 0;
  }

  get beforeBaseQuantity(): number {
    const field = this.params?.colDef?.cellRendererParams?.baseBeforeField as string;
    return this.params?.data?.[field] ?? 0;
  }

  get beforeParallelQuantity(): number {
    const field = this.params?.colDef?.cellRendererParams?.parallelBeforeField as string;
    return this.params?.data?.[field] ?? 0;
  }

  // ============================================
  // BEFORE (Antes) - Unidades de Medida
  // ============================================

  get beforeUsedUnit(): any {
    const field = this.params?.colDef?.cellRendererParams?.soldUnitBeforeField as string;
    return this.params?.data?.[field];
  }

  get beforeBaseUnit(): any {
    const field = this.params?.colDef?.cellRendererParams?.baseUnitBeforeField as string;
    return this.params?.data?.[field];
  }

  get beforeParallelUnit(): any {
    const field = this.params?.colDef?.cellRendererParams?.parallelUnitBeforeField as string;
    return this.params?.data?.[field];
  }

  // ============================================
  // Material y Flags
  // ============================================

  get material(): any {
    return (this.params?.data as any)?.material;
  }

  get isUMPActive(): boolean {
    const getUMPActive = this.params?.colDef?.cellRendererParams?.getUMPActive as (() => boolean) | undefined;
    return getUMPActive ? getUMPActive() : false;
  }
}
